import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, type ApiDevice } from '../auth/api';
import { useAuthStore } from '../auth/store';
import { BrandHero } from '../brand/BrandHero';

function formatId(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 9);
  return [a, b, c].filter(Boolean).join(' ');
}

export function ConnectPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const [hostId, setHostId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<ApiDevice[] | null>(null);

  // Reload trusted devices each visit; lightweight and avoids stale state after add/remove.
  useEffect(() => {
    if (!token) {
      setDevices(null);
      return;
    }
    let cancelled = false;
    api
      .listDevices(token)
      .then(({ devices }) => {
        if (!cancelled) setDevices(devices);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) logout();
      });
    return () => {
      cancelled = true;
    };
  }, [token, logout]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const digits = hostId.replace(/\D/g, '');
    if (digits.length !== 9) {
      setError('ID must be 9 digits');
      return;
    }
    if (password.trim().length < 4) {
      setError('Enter the password shown on the host');
      return;
    }
    setError(null);
    navigate(`/session/${digits}`, { state: { password } });
  };

  return (
    <main className="brand-stage">
      <BrandHero subtitle="Connect to your machine" />

      <aside className="brand-panel">
        <div className="brand-panel-inner">
          {user ? (
            <div className="mb-5">
              <p className="text-xs text-[var(--color-text-muted)] mb-2 text-center">
                Signed in as{' '}
                {/* `truncate` keeps very long emails from blowing out the panel width on
                    narrow phones — the email line uses its own block so the nav row below
                    stays centered regardless of email length. */}
                <span className="text-[var(--color-text)] inline-block max-w-full align-bottom truncate">
                  {user.email}
                </span>
              </p>
              <nav
                aria-label="Account actions"
                className="flex items-center justify-center flex-wrap gap-x-3 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-[var(--color-text-muted)]"
              >
                <Link to="/machines" className="hover:text-[var(--color-accent)] transition-colors">
                  machines
                </Link>
                <span className="opacity-40 select-none">·</span>
                <Link to="/security" className="hover:text-[var(--color-accent)] transition-colors">
                  security
                </Link>
                <span className="opacity-40 select-none">·</span>
                <button
                  type="button"
                  onClick={logout}
                  className="hover:text-[var(--color-accent)] transition-colors uppercase tracking-[0.2em]"
                >
                  sign out
                </button>
              </nav>
            </div>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)] mb-5 text-center">
              <Link to="/login" className="text-[var(--color-accent)] hover:underline">
                Sign in
              </Link>{' '}
              to save machines for one-click access
            </p>
          )}

          {token && devices && devices.length > 0 && (
            <section className="mb-5">
              <h2 className="text-[10px] uppercase tracking-[0.28em] text-[var(--color-text-muted)] mb-2">
                My machines
              </h2>
              <ul className="space-y-1 mb-2">
                {/* Sort: online first, then by last_connected desc, then by added_at desc.
                    Gives a TeamViewer-style "what's available right now" view. */}
                {[...devices]
                  .sort((a, b) => {
                    if (a.online !== b.online) return a.online ? -1 : 1;
                    const at = a.last_connected ? new Date(a.last_connected).getTime() : 0;
                    const bt = b.last_connected ? new Date(b.last_connected).getTime() : 0;
                    if (at !== bt) return bt - at;
                    return new Date(b.added_at).getTime() - new Date(a.added_at).getTime();
                  })
                  .slice(0, 4)
                  .map((d) => (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/session/${d.host_id}`)}
                        disabled={!d.online}
                        className={`w-full text-left bg-black/30 border rounded px-3 py-2 transition ${
                          d.online
                            ? 'border-[var(--color-border)] hover:border-[var(--color-accent)]'
                            : 'border-[var(--color-border)] opacity-60 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            aria-label={d.online ? 'online' : 'offline'}
                            className={`flex-none w-2 h-2 rounded-full ${
                              d.online ? 'bg-emerald-400' : 'bg-[var(--color-border)]'
                            }`}
                          />
                          <span className="font-medium text-sm flex-1 truncate">
                            {d.nickname || d.hostname || `Host ${d.host_id}`}
                          </span>
                          <span className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider">
                            {d.online ? 'online' : 'offline'}
                          </span>
                        </div>
                      </button>
                    </li>
                  ))}
              </ul>
              <Link
                to="/machines"
                className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-accent)] hover:underline"
              >
                manage machines →
              </Link>
            </section>
          )}

          <h2 className="brand-panel-title">Quick connect</h2>
          <form onSubmit={onSubmit} className="space-y-4" autoComplete="off">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.28em] text-[var(--color-text-muted)] mb-1.5">
                Host ID
              </span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                value={hostId}
                onChange={(e) => setHostId(formatId(e.target.value))}
                placeholder="123 456 789"
                className="input-luxe text-lg font-mono tracking-widest text-center"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.28em] text-[var(--color-text-muted)] mb-1.5">
                Password
              </span>
              <input
                type="password"
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                className="input-luxe text-lg font-mono text-center"
              />
            </label>
            {error && <p className="text-sm text-[var(--color-danger)] text-center">{error}</p>}
            <button
              type="submit"
              className="btn-gold w-full py-3 text-sm tracking-[0.25em] uppercase"
            >
              Connect
            </button>
          </form>

          {/* Secondary CTA — download the host program for the OWN computer. Mirrors the card on
              /login (same tokens + layout) so the two entry points feel cohesive. Public static
              file, no auth required. */}
          <div className="mt-6 pt-5 border-t border-[var(--color-border)]/50">
            <a
              href="/Linkora-host.zip"
              download
              className="group flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]/40 px-3.5 py-3 transition hover:border-[var(--color-accent)] hover:bg-[var(--color-surface)]/80 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/40"
            >
              <span className="flex-none w-10 h-10 rounded-lg bg-[var(--color-accent)]/12 text-[var(--color-accent)] grid place-items-center text-xl ring-1 ring-[var(--color-accent)]/25 transition group-hover:bg-[var(--color-accent)]/20">
                ⤓
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[10px] uppercase tracking-[0.28em] text-[var(--color-text-muted)]">
                  Host on this PC
                </span>
                <span className="block text-sm font-medium text-[var(--color-text)] truncate">
                  Linkora для Windows
                </span>
              </span>
              <span className="flex-none text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)] group-hover:text-[var(--color-accent)] transition">
                3.8&nbsp;МБ&nbsp;·&nbsp;.zip
              </span>
            </a>
          </div>
        </div>
      </aside>
    </main>
  );
}
